import { FormDataProvider } from "@/base/form/data-context/FormDataContext";
import { FormDatePicker } from "@/base/form/mui/FormDatePicker";
import { FormRichTextField } from "@/base/form/mui/FormRichTextField";
import { FormTextField } from "@/base/form/mui/FormTextField";
import { useT } from "@/hooks/useT";
import { CustomAccordion } from "@/layout/CustomAccordion";
import { Page } from "@/layout/Page";
import { Section } from "@/layout/Section";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { CustomDialog } from "@/layout/CustomDialog";
import { project } from "@/queries";
import { useToast } from "@/hooks/useToast";
import { useMutation } from "react-query";
import { useQ } from "@/hooks/useQ";
import EditIcon from "@mui/icons-material/Edit";
import CategoryIcon from "@mui/icons-material/Category";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import { SelectorButton, SelectorStyleButton } from "@/layout/SelectorButton";
import { ChevronDown } from "@/base/icons/chevron-down";
import { ChevronUp } from "@/base/icons/chevron-up";
import { DeleteButton } from "@/base/DeleteButton";
import diff from "fast-diff";

export const DailyLog = () => {
  const { t } = useT();
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [sortedArray, setSortedArray] = useState([]);
  const [sortBy, setSortBy] = useState("urgency");
  const [followUpOnly, setFollowUpOnly] = useState(true);
  const [logId, setLogId] = useState(null);
  const { id = "" } = useParams();
  const toast = useToast();

  const { data: dailyLogs = [], refetch } = useQ(
    `project-${id}-dailyLogs`,
    () =>
      project.getDailyLogs({
        id,
      }),
  );

  const { data: dailySummary = [], refetch: refetchSummary } = useQ(
    `project-${id}-dailySummary`,
    () =>
      project.getDailySummary({
        id,
      }),
  );

  useEffect(() => {
    if (dailyLogs.length > 0) {
      const sorted = [...dailyLogs]
        .filter((log) => {
          if (followUpOnly) {
            return log.needsFollowUp;
          }
          return true;
        })
        .sort((a, b) => {
          if (sortBy === "date") {
            return new Date(b.date) - new Date(a.date);
          } else if (sortBy === "urgency") {
            const urgencyOrder = { low: 1, medium: 2, high: 3 };
            if (!a.urgency) {
              return 1;
            }
            if (!b.urgency) {
              return -1;
            }
            return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
          } else if (sortBy === "category") {
            return t(
              `project.dailyLog.fields.category.options.${a.category}`,
            ).localeCompare(
              t(`project.dailyLog.fields.category.options.${b.category}`),
            );
          }
          return 0;
        })
        .sort((a, b) => {
          if (a.followUpCompleted && !b.followUpCompleted) return 1;
          if (!a.followUpCompleted && b.followUpCompleted) return -1;
          return 0;
        });
      setSortedArray(sorted);
    }
  }, [dailyLogs, sortBy, followUpOnly]);

  const schema = yup.object().shape({
    id: yup.string(),
    date: yup.string().required(),
    summary: yup.string(),
  });

  const formData = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      summary: "",
      date: moment(moment.now()),
    },
  });

  const { formState, reset, watch, handleSubmit, setValue } = formData;

  const date = watch("date");

  const markedDates = useMemo(() => {
    return dailySummary.map((summary) =>
      moment(summary.date).format("YYYY-MM-DD"),
    );
  }, [dailySummary]);

  const warningDates = useMemo(() => {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(`dailySummary-${id}-`),
    );
    return keys.map((key) => key.replace(`dailySummary-${id}-`, ""));
  }, [dailySummary, id, date]);

  const [showSummary, setShowSummary] = useState(true);

  const createDailySummary = useMutation(project.addDailySummary, {
    onSuccess: (data) => {
      refetchSummary();
      toast.success("Success!");
    },
  });

  const updateDailySummary = useMutation(project.updateDailySummary, {
    onSuccess: (data) => {
      refetchSummary();
      toast.success("Success!");
    },
  });

  const currentSummary = useMemo(() => {
    const dateString = moment(date).format("YYYY-MM-DD");
    return dailySummary.find(
      (summary) => moment(summary.date).format("YYYY-MM-DD") === dateString,
    );
  }, [date, dailySummary]);

  useEffect(() => {
    const dateString = moment(date).format("YYYY-MM-DD");
    const localSummary = localStorage.getItem(
      `dailySummary-${id}-${dateString}`,
    );
    const summaryForDate = dailySummary.find(
      (summary) => moment(summary.date).format("YYYY-MM-DD") === dateString,
    );
    if (localSummary) {
      const parsed = JSON.parse(localSummary);
      if (parsed.summary !== summaryForDate?.summary) {
        setValue("summary", parsed.summary || "", {
          shouldDirty: true,
        });
      } else {
        reset({
          id: summaryForDate?.id || "",
          summary: summaryForDate?.summary || "",
          date,
        });
        localStorage.removeItem(`dailySummary-${id}-${dateString}`);
      }
    } else {
      if (summaryForDate) {
        reset({
          id: summaryForDate.id,
          summary: summaryForDate.summary || "",
          date,
        });
      } else {
        reset({
          id: "",
          summary: "",
          date,
        });
      }
    }
  }, [date, currentSummary, id, reset]);

  const summary = watch("summary");

  useEffect(() => {
    if (formState.dirtyFields?.summary && summary) {
      const dateString = moment(date).format("YYYY-MM-DD");
      localStorage.setItem(
        `dailySummary-${id}-${dateString}`,
        JSON.stringify({
          summary,
          date: dateString,
        }),
      );
    }
    if (summary === currentSummary?.summary) {
      localStorage.removeItem(
        `dailySummary-${id}-${moment(date).format("YYYY-MM-DD")}`,
      );
    }
  }, [summary]);

  return (
    <Page>
      <Section title={t("project.dailyLog.title")}></Section>
      {showSummary ? (
        <FormDataProvider {...formData} formKey={"project.dailyLog.fields"}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h2">
              {t("project.dailyLog.fields.summary.title")}{" "}
            </Typography>
            <FormDatePicker
              name="date"
              minDate
              maxDate
              markedDates={markedDates}
              warningDates={warningDates}
            />{" "}
            <IconButton onClick={() => setShowSummary(false)}>
              <ChevronDown />
            </IconButton>
          </Stack>
          <FormRichTextField label={""} name="summary" fullWidth />
          <Stack direction="row" mt={1} spacing={2} justifyContent="flex-end">
            <Button
              variant="contained"
              color={formState.isDirty ? "primary" : "secondary"}
              onClick={handleSubmit((data) =>
                (currentSummary
                  ? updateDailySummary
                  : createDailySummary
                ).mutate({
                  id,
                  data: {
                    ...data,
                    id: currentSummary?.id,
                    date: moment(data.date).format("YYYY-MM-DD"),
                  },
                }),
              )}
            >
              {t("generic.button.save")}
            </Button>
          </Stack>
        </FormDataProvider>
      ) : (
        <Typography variant="h2">
          {t("project.dailyLog.fields.summary.title")}{" "}
          {`(${moment(watch("date")).format("YYYY-MM-DD")})`}
          <IconButton onClick={() => setShowSummary(true)}>
            <ChevronUp />
          </IconButton>
        </Typography>
      )}
      <Typography variant="h3" sx={{ mb: 2 }}>
        {t("project.dailyLog.description")}
      </Typography>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {t("project.dailyLog.description2")}
      </Typography>
      <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}>
        <RadioGroup
          onChange={(e) => setSortBy(e.target.value)}
          value={sortBy}
          sx={{ mr: 2 }}
        >
          <Stack direction="row" spacing={3}>
            <Stack direction="row" spacing={0}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  justifySelf: "center",
                  display: "flex",
                  alignItems: "center",
                  mr: 0.5,
                }}
              >
                {t("project.dailyLog.sortBy.label") + ":"}
              </Typography>
              <FormControlLabel
                label={t("project.dailyLog.sortBy.date")}
                value="date"
                control={<Radio />}
              />
              <FormControlLabel
                label={t("project.dailyLog.sortBy.urgency")}
                value="urgency"
                control={<Radio />}
              />
              <FormControlLabel
                label={t("project.dailyLog.sortBy.category")}
                value="category"
                control={<Radio />}
              />
            </Stack>
            <FormControlLabel
              label={t("project.dailyLog.followUpOnly")}
              control={
                <Checkbox
                  checked={followUpOnly}
                  onChange={() => setFollowUpOnly(!followUpOnly)}
                />
              }
            />
          </Stack>
        </RadioGroup>
        <Button
          variant="text"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setShowNewItemForm((s) => !s)}
        >
          {showNewItemForm
            ? t("generic.button.close")
            : t("project.dailyLog.button.new")}
        </Button>
      </Box>
      <Accordion
        dailyLogs={sortedArray}
        logId={logId}
        setLogId={setLogId}
        projectId={id}
        t={t}
        refetch={refetch}
      />
      {showNewItemForm && (
        <CustomDialog
          open={showNewItemForm}
          onClose={() => setShowNewItemForm(false)}
          title={t("project.dailyLog.title")}
        >
          <DailyLogForm
            onSuccess={() => {
              refetch();
            }}
            onClose={() => setShowNewItemForm(false)}
            id={id}
            submit={project.addDailyLog}
          />
        </CustomDialog>
      )}
    </Page>
  );
};

const Accordion = ({ dailyLogs, logId, setLogId, t, projectId, refetch }) => {
  const localT = useT();
  function handleOpenLog(id) {
    if (logId === id) {
      setLogId(null);
    } else {
      setLogId(id);
    }
  }

  return (
    <CustomAccordion
      tableView
      header={{
        expanded: false,
        columns: [
          {
            title: t("project.dailyLog.fields.title.title"),
            flex: 3,
          },

          {
            title: t("project.dailyLog.fields.category.title"),
            flex: 2,
          },

          {
            title: t("project.dailyLog.fields.urgency.title"),
            flex: 2,
          },
          {
            title: t("project.dailyLog.fields.flag.title"),
            flex: 2,
          },
          {
            title: t("project.dailyLog.fields.date.title"),
            flex: 2,
          },
        ],
      }}
      rows={dailyLogs.map((log, i) => ({
        id: log.id,
        onClick: () => handleOpenLog(log.id),
        expanded: log.id === logId,
        sx: {
          "& .MuiAccordionSummary-root": {
            opacity: log.followUpCompleted ? 0.5 : 1,
            textDecoration: log.followUpCompleted ? "line-through" : "initial",
          },
        },
        columns: [
          {
            title: log.title,
            flex: 3,
          },
          {
            title: log.category
              ? t(`project.dailyLog.fields.category.options.${log.category}`)
              : " ",
            flex: 2,
          },
          {
            title: log.urgency
              ? t(`project.dailyLog.fields.urgency.options.${log.urgency}`)
              : " ",
            flex: 2,
          },
          {
            title: log.needsFollowUp ? "Yes" : "No",
            flex: 2,
          },
          {
            title: moment(log.date).format("YYYY-MM-DD"),
            flex: 2,
          },
        ],
        details: (
          <>
            <LogDetails
              log={log.id === logId ? log : null}
              projectId={projectId}
              onClose={() => {
                setLogId(null);
                refetch();
              }}
              refetch={refetch}
              onDelete={() => refetch()}
              t={t}
            />
          </>
        ),
      }))}
    />
  );
};

export const DailyLogForm = ({ dailyLog, onClose, id, onSuccess, submit }) => {
  const { t } = useT();
  const [followUp, setFollowUp] = useState(dailyLog?.needsFollowUp || false);
  const [completed, setCompleted] = useState(
    dailyLog?.followUpCompleted || false,
  );
  const [showResponsible, setShowResponsible] = useState(false);
  const [showDueDate, setShowDueDate] = useState(false);

  const schema = yup.object().shape({
    date: yup.string().required(),
    notes: yup.string(),
    title: yup.string().required(),
    needsFollowUp: yup.boolean(),
    followUpCompleted: yup.boolean(),
    link: yup.string(),
    responsible: yup.string(),
    dueDate: yup.string(),
    category: yup
      .string()
      .oneOf([
        "risk",
        "issue",
        "decision",
        "action",
        "observation",
        "changeRequest",
        "dependency",
        "other",
      ]),
    urgency: yup.string().oneOf(["low", "medium", "high"]),
  });

  const formData = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      date: dailyLog?.date ? moment(dailyLog?.date) : moment(moment.now()),
      notes: dailyLog?.notes || "",
      title: dailyLog?.title || "",
      category: dailyLog?.category,
      responsible: dailyLog?.responsible || "",
      needsFollowUp: dailyLog?.needsFollowUp ?? false,
      followUpCompleted: dailyLog?.followUpCompleted ?? false,
      link: dailyLog?.link || "",
      urgency: dailyLog?.urgency,
      dueDate: dailyLog?.dueDate ? moment(dailyLog?.dueDate) : undefined,
    },
  });

  const toast = useToast();

  const submitLog = useMutation(submit, {
    onSuccess: (result, { data }) => {
      console.log("Received data: " + JSON.stringify(result));
      toast.success("Log created successfully");
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      console.log("Received error: " + JSON.stringify(error));
      toast.error("Error creating log");
    },
  });
  const { handleSubmit, setValue, watch, formState } = formData;

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      console.log("Form errors:", formState.errors);
    }
  }, [formState.errors]);

  const categoryValue = watch("category");
  const urgency = watch("urgency");

  const responsibleFieldRef = useRef(null);

  const handleResponsibleClick = () => {
    setShowResponsible(true);
    setTimeout(() => {
      if (responsibleFieldRef.current) {
        responsibleFieldRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        responsibleFieldRef.current.focus();
      }
    }, 100);
  };

  const handleDueDateClick = () => {
    setShowDueDate(!showDueDate);
  };

  const isnew = !dailyLog;
  const responsibleValue = watch("responsible");
  const shouldShowDueDate = showDueDate || !!watch("dueDate");
  const shouldShowResponsible = showResponsible || !!responsibleValue;

  return (
    <FormDataProvider {...formData} formKey={"project.dailyLog.fields"}>
      <form
        onSubmit={handleSubmit((data) => {
          submitLog.mutate({
            id,
            dailyLogId: isnew ? undefined : dailyLog.id,
            data: {
              ...data,
              date: moment(data.date).format("YYYY-MM-DD"),
              dueDate: data.dueDate
                ? moment(data.dueDate).format("YYYY-MM-DD")
                : undefined,
              needsFollowUp: followUp,
              followUpCompleted: completed,
            },
          });
        })}
      >
        <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <Stack>
              <Typography
                sx={{
                  fontSize: "20px !important",
                  color: "#111827 !important",
                  marginBottom: "8px !important",
                }}
              >
                {t("project.dailyLog.fields.date.title")}
                <Typography
                  component="span"
                  color="error"
                  fontSize="1.3em"
                  lineHeight="1em"
                >
                  {" "}
                  *
                </Typography>
              </Typography>
              <FormDatePicker name="date" minDate maxDate />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignSelf: "flex-end" }}>
              <SelectorButton
                name="category"
                value={categoryValue}
                onChange={(value) => {
                  setValue("category", value);
                }}
                label={t("project.dailyLog.fields.category.title")}
                icon={<CategoryIcon />}
                options={[
                  {
                    value: "risk",
                    label: t("project.dailyLog.fields.category.options.risk"),
                  },
                  {
                    value: "issue",
                    label: t("project.dailyLog.fields.category.options.issue"),
                  },
                  {
                    value: "decision",
                    label: t(
                      "project.dailyLog.fields.category.options.decision",
                    ),
                  },
                  {
                    value: "action",
                    label: t("project.dailyLog.fields.category.options.action"),
                  },
                  {
                    value: "observation",
                    label: t(
                      "project.dailyLog.fields.category.options.observation",
                    ),
                  },
                  {
                    value: "changeRequest",
                    label: t(
                      "project.dailyLog.fields.category.options.changeRequest",
                    ),
                  },
                  {
                    value: "dependency",
                    label: t(
                      "project.dailyLog.fields.category.options.dependency",
                    ),
                  },
                  {
                    value: "other",
                    label: t("project.dailyLog.fields.category.options.other"),
                  },
                ]}
              />
              <SelectorButton
                name="urgency"
                value={urgency}
                onChange={(value) => {
                  setValue("urgency", value);
                }}
                label={t("project.dailyLog.fields.urgency.title")}
                icon={<AccessAlarmIcon />}
                options={[
                  {
                    value: "high",
                    label: t("project.dailyLog.fields.urgency.options.high"),
                  },
                  {
                    value: "medium",
                    label: t("project.dailyLog.fields.urgency.options.medium"),
                  },
                  {
                    value: "low",
                    label: t("project.dailyLog.fields.urgency.options.low"),
                  },
                ]}
              />
              <SelectorButton
                name="followUp"
                value={followUp}
                onChange={(value) => {
                  setFollowUp(value);
                }}
                label={t("project.dailyLog.fields.flag.label")}
                icon={<BookmarkIcon />}
                options={[
                  {
                    value: true,
                    label: t("project.dailyLog.fields.flag.title"),
                  },
                  {
                    value: false,
                    label: t("project.dailyLog.fields.flag.notNeeded"),
                  },
                ]}
              />
              {followUp && (
                <SelectorButton
                  name="completed"
                  value={completed}
                  onChange={(value) => {
                    setCompleted(value);
                  }}
                  icon={<DoneAllIcon />}
                  label={t("project.dailyLog.fields.flag.completed")}
                  options={[
                    {
                      value: true,
                      label: t("project.dailyLog.fields.flag.completed"),
                    },
                    {
                      value: false,
                      label: t("project.dailyLog.fields.flag.notCompleted"),
                    },
                  ]}
                />
              )}
              {followUp && (
                <SelectorStyleButton
                  name="dueDate"
                  onClick={handleDueDateClick}
                  label={t("project.dailyLog.fields.dueDate.title")}
                  icon={<EventIcon />}
                />
              )}
              <SelectorStyleButton
                name="responsible"
                onClick={handleResponsibleClick}
                label={t("project.dailyLog.fields.responsible.title")}
                icon={<PersonIcon />}
              />
            </Stack>
          </Stack>

          {shouldShowDueDate && (
            <Stack>
              <Typography
                sx={{
                  fontSize: "20px !important",
                  color: "#111827 !important",
                  marginBottom: "8px !important",
                }}
              >
                {t("project.dailyLog.fields.dueDate.title")}
              </Typography>

              <FormDatePicker name="dueDate" minDate maxDate />
            </Stack>
          )}

          <Stack>
            <Stack direction="row" spacing={2}>
              <Stack sx={{ width: "100%" }}>
                <FormTextField required name={"title"} fullWidth />
              </Stack>
            </Stack>
          </Stack>

          {shouldShowResponsible && (
            <FormTextField
              name={"responsible"}
              fullWidth
              textFieldRef={responsibleFieldRef}
              onBlur={() => setShowResponsible(false)}
              onFocus={() => setShowResponsible(true)}
              usePreventDefaultOnHelper
            />
          )}

          <FormRichTextField name={"notes"} fullWidth />

          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <FormTextField
              name={"link"}
              fullWidth
              placeholder={t("project.dailyLog.fields.link.description")}
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ width: "100%", mt: 2 }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => onClose()}
          >
            {t("generic.button.cancel")}
          </Button>
          <Button type="submit" variant="contained">
            {t("generic.button.save")}
          </Button>
        </Stack>
      </form>
    </FormDataProvider>
  );
};

const DisplayContent = ({ log, item, t }) => {
  return (
    <Stack>
      <Typography variant="h3">
        {t(`project.dailyLog.fields.${item}.title`)}
      </Typography>
      <Typography
        variant="h4"
        dangerouslySetInnerHTML={{
          __html: log[item] ?? "",
        }}
      />
    </Stack>
  );
};

const CommentSection = ({ projectId, dailyLog, refetch }) => {
  const { t } = useT();
  const toast = useToast();

  const comments = dailyLog?.comments || [];

  const { data: changeHistory = [], refetch: refetchChangeHistory } = useQ(
    `project-${projectId}-dailyLogs-${dailyLog?.id}-changeHistory`,
    () =>
      project.getDailyLogChangeHistory({
        id: projectId,
        dailyLogId: dailyLog?.id,
      }),
  );
  const [showHistory, setShowHistory] = useState(false);

  const commentsAndHistory = [
    ...comments,
    ...changeHistory.map((ch) => ({ ...ch, isHistory: true })),
  ];

  const schema = yup.object().shape({
    id: yup.string(),
    comment: yup.string(),
    date: yup.string().required(),
  });

  const formData = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      comment: "",
      date: moment(),
    },
  });

  const addComment = useMutation(project.addDailyLogComment, {
    onSuccess: (data) => {
      reset();
      toast.success(t("project.dailyLog.comments.add.success"));
      refetch?.();
    },
    onError: () => {
      toast.error(t("project.dailyLog.comments.add.error"));
    },
  });

  const { reset, handleSubmit } = formData;

  const [hoveredCommentId, setHoveredCommentId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const deleteLog = useMutation(project.deleteDailyLogComment, {
    onSuccess: () => {
      refetch?.();
      toast.success(t("project.dailyLog.delete.success"));
    },
    onError: () => {
      toast.error(t("project.dailyLog.delete.error"));
    },
  });

  function handleDeleteLog(id, logId, commentId) {
    deleteLog.mutate({ id, dailyLogId: logId, commentId });
  }

  return (
    <Box sx={{ border: "2px solid #e0e0e0", borderRadius: 3, p: 3, mt: 2 }}>
      <Stack spacing={1}>
        <Typography variant="h3">
          {t("project.dailyLog.comments.title")}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Checkbox
                checked={showHistory}
                onChange={(e) => setShowHistory(e.target.checked)}
              />
            }
            label={t("Show change history")}
          />
        </Stack>
        <Stack spacing={0.5}>
          {(showHistory ? commentsAndHistory : comments).length === 0 ? (
            <Typography variant="h4" color="text.secondary">
              {t("project.dailyLog.comments.empty")}
            </Typography>
          ) : (
            (showHistory ? commentsAndHistory : comments)
              .sort(
                (a, b) =>
                  new Date(a.date ?? a.timestamp) -
                  new Date(b.date ?? b.timestamp),
              )
              .map((comment) =>
                comment.isHistory ? (
                  <Box
                    sx={{
                      p: 1,
                      pl: 2,
                      border: "1px solid #e0e0e0",
                      width: "100%",
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                      position: "relative",
                    }}
                  >
                    {(() => {
                      const historyItems = commentsAndHistory
                        .filter((c) => c.isHistory)
                        .sort(
                          (a, b) =>
                            new Date(a.date ?? a.timestamp) -
                            new Date(b.date ?? b.timestamp),
                        );
                      const idx = historyItems.findIndex((h) => h === comment);
                      const prev = idx > 0 ? historyItems[idx - 1].item : null;

                      return Object.entries(comment.item)
                        .filter(([key, value]) => {
                          if (value === "<p></p>" || value === "") {
                            return false;
                          }
                          if (!prev) {
                            return true;
                          }
                          return prev[key] !== value;
                        })
                        .map(([key, value]) => {
                          const prevValue = prev?.[key] ?? "";
                          let diffMarkup = value ?? "";

                          if (
                            typeof value === "string" &&
                            typeof prevValue === "string"
                          ) {
                            const diffs = diff(prevValue, value);
                            diffMarkup = diffs
                              .map(([type, text]) => {
                                if (type === 0) return text; // unchanged
                                if (type === -1)
                                  return `<del style="background:#ffcccc;">${text}</del>`; // removed
                                if (type === 1)
                                  return `<ins style="background:lightgreen;">${text}</ins>`; // added
                                return text;
                              })
                              .join("");
                          }

                          return (
                            <Stack
                              key={key}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t(
                                  key === "followUpCompleted"
                                    ? "project.dailyLog.fields.flag.completed"
                                    : `project.dailyLog.fields.${
                                        key === "needsFollowUp" ? "flag" : key
                                      }.title`,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="success.main"
                                dangerouslySetInnerHTML={{ __html: diffMarkup }}
                              />
                            </Stack>
                          );
                        });
                    })()}
                  </Box>
                ) : (
                  <Box
                    key={comment.id}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      p: 1,
                      bgcolor: "#fafafa",
                      position: "relative",
                    }}
                    onMouseEnter={() => setHoveredCommentId(comment.id)}
                    onMouseLeave={() => setHoveredCommentId(null)}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {comment.date}
                      </Typography>
                      {hoveredCommentId === comment.id && (
                        <Stack direction="row" spacing={1}>
                          <DeleteButton
                            onConfirm={() => {
                              handleDeleteLog(
                                projectId,
                                dailyLog?.id,
                                comment.id,
                              );
                            }}
                            content={t(
                              "project.dailyLog.comments.delete.title",
                            )}
                            icon={<></>}
                          />

                          <Button
                            variant="text"
                            color="secondary"
                            startIcon={<EditIcon />}
                            onClick={() =>
                              setEditingId(
                                editingId === comment.id ? null : comment.id,
                              )
                            }
                          >
                            {t("generic.button.edit")}
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                    {editingId === comment.id && (
                      <CustomDialog
                        open={editingId === comment.id}
                        title={`${t(
                          "project.dailyLog.comments.edit",
                        )} (${moment(comment?.date).format("YYYY-MM-DD")})`}
                        onClose={() => setEditingId(null)}
                      >
                        <EditCommentForm
                          comment={comment}
                          projectId={projectId}
                          dailyLog={dailyLog}
                          refetch={refetch}
                          onClose={() => setEditingId(null)}
                        />
                      </CustomDialog>
                    )}
                    <Typography
                      variant="body1"
                      dangerouslySetInnerHTML={{
                        __html: comment.text ?? "",
                      }}
                    />
                  </Box>
                ),
              )
          )}
        </Stack>
        <Box sx={{ mt: 2 }}>
          <FormDataProvider
            {...formData}
            formKey={"project.dailyLog.comments.fields"}
          >
            <form
              onSubmit={handleSubmit((data) => {
                addComment.mutate({
                  id: projectId,
                  dailyLogId: dailyLog?.id,
                  data: {
                    ...data,
                    date: moment(data.date).format("YYYY-MM-DD"),
                  },
                });
              })}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h2">
                  {t("project.dailyLog.comments.fields.comment.title")}{" "}
                </Typography>
                <FormDatePicker name="date" minDate maxDate />{" "}
              </Stack>
              <FormRichTextField
                label={""}
                help={""}
                name="comment"
                fullWidth
              />
              <Stack spacing={1} alignItems="flex-end" pt={1}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("project.dailyLog.comments.add")}
                </Button>
              </Stack>
            </form>
          </FormDataProvider>
        </Box>
      </Stack>
    </Box>
  );
};

const EditCommentForm = ({
  comment,
  projectId,
  dailyLog,
  refetch,
  onClose,
}) => {
  const { t } = useT();
  const toast = useToast();
  const schema = yup.object().shape({
    id: yup.string(),
    comment: yup.string(),
    date: yup.string().required(),
  });

  const formData = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      comment: comment.text,
      date: moment(comment.date),
    },
  });

  const editComment = useMutation(project.editDailyLogComment, {
    onSuccess: (data) => {
      toast.success(t("project.dailyLog.comments.edit.success"));
      refetch?.();
      onClose();
    },
    onError: () => {
      toast.error(t("project.dailyLog.comments.edit.error"));
    },
  });

  const { handleSubmit } = formData;

  return (
    <FormDataProvider
      {...formData}
      formKey={"project.dailyLog.comments.fields"}
    >
      <form
        onSubmit={handleSubmit((data) => {
          editComment.mutate({
            id: projectId,
            dailyLogId: dailyLog?.id,
            commentId: comment.id,
            data: {
              ...data,
              date: moment(data.date).format("YYYY-MM-DD"),
            },
          });
        })}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h2">
            {t("project.dailyLog.comments.fields.comment.title")}{" "}
          </Typography>
          <FormDatePicker name="date" minDate maxDate />{" "}
        </Stack>
        <FormRichTextField label={""} help={""} name="comment" fullWidth />
        <Stack direction="row" spacing={1} justifyContent="flex-end" pt={1}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => onClose()}
          >
            {t("generic.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            {t("project.dailyLog.comments.edit")}
          </Button>
        </Stack>
      </form>
    </FormDataProvider>
  );
};

const LogDetails = ({ log, onClose, t, projectId, onDelete, refetch }) => {
  const toast = useToast();
  const [editMode, setEditMode] = useState(false);

  const deleteLog = useMutation(project.deleteDailyLog, {
    onSuccess: () => {
      onDelete();
      toast.success(t("project.dailyLog.delete.success"));
    },
    onError: () => {
      toast.error(t("project.dailyLog.delete.error"));
    },
  });

  function handleDeleteLog(id, logId) {
    deleteLog.mutate({ id, dailyLogId: logId });
  }

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack spacing={2} direction="row" justifyContent="space-between">
        <Typography variant="h2">
          {log?.title} ({moment(log?.date).format("YYYY-MM-DD")})
        </Typography>
        <CustomDialog
          open={editMode}
          title={`${log?.title} (${moment(log?.date).format("YYYY-MM-DD")})`}
          onClose={() => setEditMode(false)}
        >
          <DailyLogForm
            dailyLog={log}
            onClose={() => setEditMode(false)}
            id={projectId}
            onSuccess={() => {
              onClose();
            }}
            submit={project.updateDailyLog}
          />
        </CustomDialog>
        <Stack direction="row" spacing={1}>
          <DeleteButton
            onConfirm={() => {
              handleDeleteLog(projectId, log.id);
            }}
            content={t("project.dailyLog.delete.title")}
          />
          <Button
            variant="text"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={() => setEditMode((s) => !s)}
          >
            {t("generic.button.edit")}
          </Button>
        </Stack>
      </Stack>
      {log?.category && <DisplayContent log={log} item="category" t={t} />}
      {log?.urgency && <DisplayContent log={log} item="urgency" t={t} />}
      {log?.link && <DisplayContent log={log} item="link" t={t} />}
      {log?.responsible && (
        <DisplayContent log={log} item="responsible" t={t} />
      )}
      {log?.dueDate && <DisplayContent log={log} item="dueDate" t={t} />}
      <Stack>
        <Typography variant="h3">
          {t("project.dailyLog.fields.flag.title")}
        </Typography>
        <Typography variant="h4">{log?.flag ? "Yes" : "No"}</Typography>
      </Stack>

      {log?.notes && <DisplayContent log={log} item="notes" t={t} />}

      <CommentSection projectId={projectId} dailyLog={log} refetch={refetch} />
    </Stack>
  );
};
